
import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useWaitingBookingsApi } from './waiting-bookings.hook'
import { useSlotsTrackerApi } from './slots-tracker.hook'

interface AssignBookingApiPayload {
  slotId: number
  bookingId: number
}

interface AssignBookingApiOptions {
  onStart?: () => void
  onSuccess?: () => void
  onError?: (message: string) => void
  onFinish?: () => void
}

export const useAssignBookingApi = async (
  payload: AssignBookingApiPayload,
  options?: AssignBookingApiOptions,
): Promise<boolean> => {
  const { $axios } = useNuxtApp()

  const url = useAdminRoutes('admin.demo-slots.assign-booking', { tutor_slot: payload.slotId })

  const commonStore = useCommonStore()
  const toast = useToast()

  const setError = (message: string): void => {
    options?.onError?.(message)
    toast.error(message)
  }

  options?.onStart?.()
  commonStore.setLoading(true)

  if (!url) {
    setError('Unexpected error. Invalid request URL.')
    commonStore.setLoading(false)
    options?.onFinish?.()
    return false
  }

  try {
    const { data } = await $axios.post(url, { booking_id: payload.bookingId })

    if (!data.success) {
      setError(data.message)
      return false
    }

    await Promise.all([
      useSlotsTrackerApi(),
      useWaitingBookingsApi(),
    ])

    toast.success('Booking assigned successfully')
    options?.onSuccess?.()
    return true
  } catch (err: unknown) {
    setError(getAxiosErrorMessage(err))
    return false
  } finally {
    commonStore.setLoading(false)
    options?.onFinish?.()
  }
}
